import {
  Avatar,
  // Badge, // punto del sidebar desactivado, ahora se usa el dock
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  styled,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import { getPages, getSideBarOptions, getToolkitWidgets } from "../SidebarToolkit/sidebar.config"; //New imports --------------------------------------------------------
import { SidebarVariant } from "../SidebarToolkit/sidebarVariants";
import { HeadlessModalTemplates, useSidebarConnect } from "../SidebarToolkit/useSidebarConnect";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { Cancel, ExpandLess, ExpandMore, NotificationsActiveOutlined, ViewSidebar } from "@mui/icons-material";
import { appBarHeight } from "../../appbar/AppBar";

import { IRoles } from "../../../utils";
import { ConnectBusinessExit } from "../../ConnectBusinessExit";
import { sidebarTheme } from "../SidebarToolkit/proposals/proposalIndex";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { colors as muiColors } from "../../../theme/muiColors";
import { BenefitPreviewGate } from "../../../permissions/benefits/components/gates/BenefitPreviewGate";
import { EShowMode } from "../../../permissions/benefits/enums/benefits.enums";
import { useAppSelector } from "../../../redux/hooks/redux";
import { ESubscriptionStatus } from "../../../utils";
import { useModals } from "../../../context/ModalContext";
import { CompanySwitcher } from "../CompanySwitcher";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useNotificationCountByCompany } from "./hook/useNotificationCountByCompany";
import { selectUserCompanies } from "src/redux/features/user/selectors/userState.selectors";

const colors = {
  info: { base: "#0ea5e9", 50: "#f0f9ff", 100: "#e0f2fe", 400: "#38bdf8", 700: "#0369a1" },
  success: { 50: "#f0fdf4", 100: "#dcfce7", 400: "#4ade80", 600: "#16a34a" },
  orange: { 50: "#fff7ed", 100: "#fed7aa", 400: "#fb923c", 600: "#ea580c" },
  warning: { 50: "#fffbeb", 100: "#fef3c7", 400: "#f59e0b" },
  gray: { base: "#6b7280", 100: "#f3f4f6" },
};
export const drawerWidth = {
  collapsed: {
    md: 70,
    lg: 100,
  },
  expanded: {
    md: 240,
    lg: 290,
  },
};

const SIDEBAR_TOP_GAP = 120; // New gap height for the upcoming component (80-100px)

const SIDEBAR_VARIANT = SidebarVariant.PROP2; //To change the theme, change this -----------------------------------------------------------------------

// These are the styled components ----------------------------------------------------------------------------------

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => !["open", "sidebarVariant"].includes(prop),
})(({ theme, open, sidebarVariant }) => {
  // Definimos los anchos dinámicos
  const widthMd = open ? drawerWidth.expanded.md : drawerWidth.collapsed.md;
  const widthLg = open ? drawerWidth.expanded.lg : drawerWidth.collapsed.lg;

  return {
    zIndex: 1,
    // Aplicamos los anchos por breakpoint
    width: widthLg, // Default (lg en adelante)
    [theme.breakpoints.only("md")]: {
      width: widthMd,
    },

    transition: theme.transitions.create(["width", "left"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    flexShrink: 0,
    border: "1px solid transparent", //transparent
    height: `calc(100vh - ${appBarHeight + SIDEBAR_TOP_GAP + 20}px)`,
    padding: open ? "0 10px 0 10px" : 0,
    top: `${appBarHeight + SIDEBAR_TOP_GAP}px`,
    left: "10px",
    position: "fixed",

    display: {
      xs: "none",
      sm: "none",
      md: "flex",
    },

    justifyContent: "center",

    /* ===== DRAWER PAPER ===== */
    "& .MuiDrawer-paper": {
      position: "relative",
      top: 0,
      height: "100%",

      width: "100%",
      overflowY: "hidden",
      overflowX: "hidden",
      overflow: "visible",

      boxSizing: "border-box",
      borderRadius: "25px",

      display: "flex",
      flexDirection: "column",

      background: "transparent",

      border: "1px solid transparent", //transparent

      "&:hover": {
        // border: `1px solid ${colors.orange[100]}`,
        // boxShadow: "-8px 0 20px rgba(251, 146, 60, 0.12), 0 12px 32px rgba(251, 146, 60, 0.15)", check this later
      },

      transition: theme.transitions.create(["box-shadow", "border"], {
        easing: theme.transitions.easing.easeInOut,
        duration: theme.transitions.duration.standard,
      }),
    },
  };
});

const ToolkitSection = styled(Box, {
  //DONE
  shouldForwardProp: (prop) => prop !== "sidebarVariant",
})(({ sidebarVariant }) => {
  const t = sidebarTheme[sidebarVariant] ?? sidebarTheme[SidebarVariant.PROP_1];

  return {
    /* ===== THEME-DRIVEN ===== */
    backgroundColor: t.ToolkitSection.background,

    /* ===== STRUCTURAL (UNCHANGED) ===== */
    width: "calc(100% - 16px)",
    margin: "0 8px",
    marginTop: "-4px",
    padding: "8px 0 0 0px",

    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",

    borderTop: "none",
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    boxSizing: "border-box",
  };
});

const ToolkitItem = styled(ListItem, {
  //DONE
  shouldForwardProp: (prop) => prop !== "sidebarVariant",
})(({ sidebarVariant }) => {
  const t = sidebarTheme[sidebarVariant] ?? sidebarTheme[SidebarVariant.PROP_1];

  return {
    /* ===== STRUCTURAL (UNCHANGED) ===== */
    padding: "6px 8px",
    marginBottom: "2px",
    borderRadius: 0,
    transition: "box-shadow 220ms ease, transform 200ms ease",
    position: "relative",
    overflow: "visible",
    cursor: "pointer",
    margin: "0 4px 4px 4px",
    width: "calc(100% - 18px)",
    border: "1px solid transparent",
    transition: "0.2s all ease-in-out",

    direction: "ltr",

    /* ===== THEME-DRIVEN ===== */
    "&:hover": {
      transform: `translateY(${t.ToolkitItem.lift?.hover ?? "-1px"})`,
      borderBottom: t.ToolkitItem.borderBottom,
      boxShadow: t.ToolkitItem.shadow.hover,

      "& svg": {
        color: colors.orange[400],
      },
    },
  };
});

const ToolkitIcon = styled(Box, {
  shouldForwardProp: (prop) => !["sidebarVariant", "hoverColor", "expandedItem"].includes(prop),
})(({ sidebarVariant, hoverColor, expandedItem }) => {
  const t = sidebarTheme[sidebarVariant] ?? sidebarTheme[SidebarVariant.PROP_1];

  return {
    /* ===== STRUCTURAL (UNCHANGED) ===== */
    width: 40,
    height: 40,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
    marginRight: expandedItem ? "12px" : 0,
    cursor: "pointer",

    /* ===== THEME-DRIVEN ===== */
    backgroundColor: t.ToolkitIcon.background,
    border: t.ToolkitIcon.border,
    // border: "1px solid blue",
    boxShadow: t.ToolkitIcon.shadow,

    "& svg": {
      color: t.ToolkitIcon.icon.default,
      transition: "color 200ms ease",
    },
  };
});

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => !["expandItem", "isSubscription", "isNormal"].includes(prop),
})(({ selected, expandItem, isSubscription, colors }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  borderRadius: 4,
  padding: "8px",
  justifyContent: "center",
  margin: "2px 0",
  width: "100%",
  direction: "ltr",
  transition: "all 220ms ease",
  border: "1px solid transparent",
  boxShadow: "0 2px 6px rgba(12,12,12,.06)",

  // --- INICIAL ---
  backgroundColor: selected ? (isSubscription ? "#0BF468" : "var(--fx-colors-warning-100, #FFEDD5)") : "var(--fx-surface-muted, #F1F5F9)",

  "&::before": {
    content: isSubscription && !selected ? '""' : "none",
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: expandItem ? 8 : 4,
    backgroundColor: "#0BF468",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    zIndex: 1,
  },

  // --- HOVER ---
  "&:hover": {
    boxShadow: "0 8px 24px rgba(12,12,12,.12)",
    backgroundColor: isSubscription ? "#63F89F" : "var(--fx-colors-warning-100, #FFEDD5)",

    "& .MuiListItemText-root, & .MuiListItemIcon-root": {
      color: "var(--fx-surface-text, #0F172A)",
    },
    "&::before": { display: "none" },
  },

  ...(selected && {
    backgroundColor: isSubscription ? "#33F582 !important" : "var(--fx-colors-warning-100, #FFEDD5) !important",
    boxShadow: "0 8px 24px rgba(12,12,12,.12)",

    "& .MuiListItemText-root, & .MuiListItemIcon-root": {
      color: isSubscription ? "#FFFFFF" : "var(--fx-surface-text, #0F172A)",
    },
    "&::before": { display: "none" },
  }),

  // Color de texto/iconos base
  "& .MuiListItemText-root, & .MuiListItemIcon-root": {
    color: selected ? "var(--fx-surface-text, #0F172A)" : "var(--fx-surface-text, #0F172A)",
  },
}));

const CollapseToggleButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  position: "fixed",
  top: `calc(${appBarHeight}px + (100vh - ${appBarHeight}px) / 2)`,
  left: `calc(10px + ${open ? drawerWidth.expanded.lg : drawerWidth.collapsed.lg}px)`,
  [theme.breakpoints.only("md")]: {
    left: `calc(10px + ${open ? drawerWidth.expanded.md : drawerWidth.collapsed.md}px)`,
  },
  alignSelf: "flex-start",

  transition: theme.transitions.create("left", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),

  // marginLeft: open ? (drawerWidth.max / 2) : (drawerWidth.min / 2),
  transform: "translate(-50%, -50%)",

  zIndex: 1,

  backgroundColor: "var(--fx-surface-base, #FFFFFF)",
  // boxShadow: "0 2px 6px rgba(12,12,12,.06)",
  transition: "all 0.2s ease",

  "&:hover": {
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    boxShadow: "0 4px 4px rgba(12,12,12,.12)",
  },
}));

const SidebarWrapper = styled("div", {
  shouldForwardProp: (prop) => !["open"].includes(prop),
})(({ theme, open }) => ({
  position: "relative",
  display: "flex",
  alignItems: "stretch",
  width: open ? drawerWidth.expanded.lg : drawerWidth.collapsed.lg,
  [theme.breakpoints.only("md")]: {
    width: open ? drawerWidth.expanded.md : drawerWidth.collapsed.md,
  },

  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  height: "100%",
  minWidth: 0,
  marginLeft: "10px",
}));

// -----------------------------------------------------------------------------------------------------------------------

export const SidebarConnect = ({ basePath }) => {
  const navigate = useNavigate();
  const {
    role,
    isMediumScreen,
    openExit,
    setOpenExit,
    continueAction,
    setContinueAction,
    toolkitExpanded,
    setToolkitExpanded,
    organizationExpanded,
    setOrganizationExpanded,
    expandItem,
    handleExpand,
    handlePageItemClick,
    handleOpenWidget,
    handleOpenOrganizationWidget,
    isPageActive,
    isMinimized,
    guardedNavigate,
    handleBusiness,
    handleHome,
    handleOpenSidebarModal,
    handleFlowHub,
  } = useSidebarConnect({ basePath });

  const { openModal } = useModals();

  const handlers = { guardedNavigate, handleBusiness, handleHome, handleFlowHub };

  const toolkitWidgetsNew = React.useMemo(() => getToolkitWidgets(colors), []);

  const pagesNew = React.useMemo(() => getPages(role, handlers), [role]);

  const companyRoles = [IRoles.Business, IRoles.Admin, IRoles.VR, IRoles.Manager, IRoles.Other, IRoles.SuperAdmin];
  const canShowCompanySwitcher = companyRoles.includes(role);

  const isTooShort = useMediaQuery("(max-height: 600px)");
  const benefitsSubscriptions = useAppSelector((state) => state.benefits.subscriptions?.subscriptions) ?? [];
  const userStateSubscriptions = useAppSelector((state) => state.userState?.subscriptionInfo?.subscriptions) ?? [];
  const allSubscriptions = userStateSubscriptions.length > 0 ? userStateSubscriptions : benefitsSubscriptions;

  const mainSubscription = useAppSelector((state) => state.userState?.subscriptionInfo?.subscription);
  const mainSubscriptionStatus = mainSubscription?.status;
  const isMainFailed = mainSubscriptionStatus === ESubscriptionStatus.FAILED || mainSubscriptionStatus === ESubscriptionStatus.SUSPENDED;

  const hasFailedSecondary = allSubscriptions.some(
    (sub) => sub?.subscription?.status === ESubscriptionStatus.FAILED || sub?.subscription?.status === ESubscriptionStatus.SUSPENDED,
  );

  const failedSubscription = isMainFailed || hasFailedSecondary;

  const { data: notificationsByCompany = [] } = useNotificationCountByCompany();

  const activeCompanyId = useAppSelector((state) => state.userState?.company?.id);

  const activeCompanyData = notificationsByCompany.find((item) => String(item.companyId) === String(activeCompanyId));

  const activeCompanyNotifications = Number(
    notificationsByCompany.find((item) => String(item.companyId) === String(activeCompanyId))?.notifications ?? 0,
  );
  const companies = useAppSelector(selectUserCompanies) ?? [];

  const [otherCompaniesAnchor, setOtherCompaniesAnchor] = useState(null);

  const otherCompanyItems = companies
    .filter((membership) => String(membership.companyId) !== String(activeCompanyId))
    .map((membership) => {
      const notifications = Number(
        notificationsByCompany.find((item) => String(item.companyId) === String(membership.companyId))?.notifications ?? 0,
      );

      const companyName = membership.company?.businessName ?? `Company #${membership.companyId}`;

      return {
        id: membership.companyId,
        name: companyName,
        avatar: membership.company?.avatar ?? membership.company?.image ?? null,
        notifications,
      };
    })
    .filter((company) => company.notifications > 0);

  const otherCompaniesNotifications = otherCompanyItems.reduce((total, company) => total + company.notifications, 0);

  const hasOtherCompaniesNotifications = otherCompaniesNotifications > 0;

  const openOtherCompanies = Boolean(otherCompaniesAnchor);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, ease: "easeInOut" }}>
      {!isMediumScreen && !isTooShort && (
        <>
          <SidebarWrapper open={expandItem}>
            <Box
              sx={{
                position: "fixed",
                top: `${appBarHeight}px`,
                left: "10px",

                width: expandItem
                  ? { md: drawerWidth.expanded.md, lg: drawerWidth.expanded.lg }
                  : { md: drawerWidth.collapsed.md, lg: drawerWidth.collapsed.lg },
                height: `${SIDEBAR_TOP_GAP}px`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1,
                transition: (theme) =>
                  theme.transitions.create("width", {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
              }}
            >
              {canShowCompanySwitcher && (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    mb: 2,
                  }}
                >
                  <CompanySwitcher
                    isExpanded={expandItem}
                    notificationsCount={activeCompanyNotifications}
                    onNotificationsClick={(companyId) =>
                      navigate(companyId ? `${basePath}/notifications-center?companyId=${companyId}` : `${basePath}/notifications-center`)
                    }
                  />

                  {expandItem && hasOtherCompaniesNotifications && (
                    <Box
                      component={motion.button}
                      type="button"
                      animate={{
                        scale: [1, 1.025, 1],
                      }}
                      transition={{
                        duration: 1.6,
                        repeat: Infinity,
                        repeatDelay: 2,
                      }}
                      onClick={(event) => setOtherCompaniesAnchor(event.currentTarget)}
                      sx={{
                        border: "1px solid #dbeafe",
                        backgroundColor: "var(--fx-colors-info-50, #EFF6FF)",
                        borderRadius: "999px",
                        backgroundColor: "var(--fx-colors-info-50, #EFF6FF)",
                        borderRadius: "999px",
                        px: 2,
                        py: 0.9,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.75,
                        color: "#1e40af",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        boxShadow: "0 0 0 0 rgba(147, 197, 253, 0.4)",
                        whiteSpace: "nowrap",
                        mb: 2,

                        "&:hover": {
                          backgroundColor: "var(--fx-colors-info-100, #DBEAFE)",
                        },
                      }}
                    >
                      <NotificationsActiveOutlined
                        sx={{
                          fontSize: 17,
                          color: "#2563eb",
                        }}
                      />

                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#1e40af",
                        }}
                      >
                        {otherCompaniesNotifications} notifications in other companies
                      </Typography>

                      {openOtherCompanies ? (
                        <ExpandLess
                          sx={{
                            fontSize: 17,
                            color: "#2563eb",
                          }}
                        />
                      ) : (
                        <ExpandMore
                          sx={{
                            fontSize: 17,
                            color: "#2563eb",
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                alignItems: "stretch",
                width: "auto",
                minWidth: 0,
                marginLeft: "10px",
                // border: "1px solid green"
              }}
            >
              <StyledDrawer open={expandItem} variant="permanent" sidebarVariant={SIDEBAR_VARIANT} anchor="left">
                <Box
                  sx={{
                    border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
                    borderRadius: "16px",
                    background: "var(--fx-surface-base, #FFFFFF)",
                    // boxShadow: "-6px 6px 10px 0px rgba(0, 0, 0, 0.10)",
                    transition: "0.2s all ease-in",

                    "&:hover": {
                      border: `1px solid ${colors.orange[100]}`,
                    },

                    pb: 0,
                    flex: 1,
                    overflow: "auto",
                    direction: "rtl",
                    display: "flex",
                    flexDirection: "column",
                    "&::-webkit-scrollbar": { width: 3 },
                    "&::-webkit-scrollbar-track": { background: "transparent", margin: "30px 0px" },
                    "&::-webkit-scrollbar-thumb": {
                      // backgroundColor: t.Drawer.scrollbar?.thumb,
                      borderRadius: 10,
                      opacity: 0,
                      backgroundColor: `${muiColors.orange[100]} !important`,
                    },
                    "::-webkit-scrollbar-button": { display: "none" },
                  }}
                >
                  <List
                    sx={{
                      // border: "1px solid red",
                      borderRadius: "25px",
                      margin: "10px 5px 0px 5px",
                      pb: 0,
                      display: "flex",
                      flexDirection: "column",
                      height: "fit-content",
                    }}
                  >
                    {pagesNew
                      .filter((page) => page.roles.includes(role))
                      .map((page, index) => (
                        <React.Fragment key={`${role?.type ?? role ?? "page"}-${index}`}>
                          <ListItem>
                            <StyledListItemButton
                              expandItem={expandItem}
                              selected={isPageActive(page, index)}
                              isSubscription={page.name?.business === "Subscriptions"}
                              onClick={() => handlePageItemClick(index, page, page.callback)}
                            >
                              <ListItemIcon
                                sx={{
                                  color: colors.gray.base,
                                  minWidth: expandItem ? 40 : "auto",
                                  justifyContent: expandItem ? "flex-start" : "center",
                                  ml: expandItem ? 1 : 0.6,
                                }}
                              >
                                {page.icon}
                              </ListItemIcon>
                              <ListItemText
                                sx={{
                                  opacity: expandItem ? 1 : 0,
                                  color: page.name?.business === "Subscriptions" ? "var(--fx-surface-text, #0F172A)" : colors.gray.base,
                                  transition: (theme) =>
                                    theme.transitions.create("opacity", {
                                      easing: theme.transitions.easing.sharp,
                                      duration: theme.transitions.duration.enteringScreen,
                                    }),
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  "& .MuiListItemText-primary": {
                                    fontWeight: 600,
                                    fontSize: 14,
                                  },
                                }}
                              >
                                {page.name[role]}
                              </ListItemText>

                              {page.name?.business === "Subscriptions" && failedSubscription && (
                                <ErrorOutlineIcon
                                  sx={{
                                    color: "error.main",
                                    fontSize: 20,
                                    ml: 1,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                              {page.isToolkit && expandItem && (
                                <Box
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setToolkitExpanded((prev) => !prev);
                                  }}
                                  sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                                >
                                  {toolkitExpanded ? <ExpandLess /> : <ExpandMore />}
                                </Box>
                              )}
                              {page.isOrganization && expandItem && (
                                <Box
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOrganizationExpanded((prev) => !prev);
                                  }}
                                  sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                                >
                                  {organizationExpanded ? <ExpandLess /> : <ExpandMore />}
                                </Box>
                              )}
                            </StyledListItemButton>
                          </ListItem>
                          {/* Toolkit Section - Collapsed */}
                          {page.isToolkit && !expandItem && (
                            <Box sx={{ ml: 0 }}>
                              {toolkitWidgetsNew
                                .filter((widget) => widget.key.startsWith("Dashboard") && (!widget.roles || widget.roles.includes(role)))
                                .map((widget) => {
                                  // const minimized = isMinimized(widget.key); // punto del sidebar desactivado
                                  return (
                                    <ListItem
                                      key={widget.key}
                                      sx={{
                                        my: 2,
                                        p: 0,
                                        position: "relative",
                                        display: "flex",
                                        flexDirection: "row",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Tooltip
                                        title={widget.label}
                                        placement="right"
                                        PopperProps={{
                                          sx: { zIndex: 99999 },
                                          modifiers: [
                                            {
                                              name: "offset",
                                              options: {
                                                offset: [0, -5],
                                              },
                                            },
                                          ],
                                        }}
                                      >
                                        {widget.benefit ? (
                                          <BenefitPreviewGate
                                            benefit={widget.benefit}
                                            featureName={widget.label}
                                            onPreview={() => handleOpenWidget({ ...widget, isPreviewMode: true })}
                                            overlay={false}
                                            showWhenBlocked={EShowMode.NULL}
                                          >
                                            {/* punto del sidebar desactivado, ahora se usa el dock */}
                                            <ToolkitIcon
                                              expandedItem={false}
                                              sidebarVariant={SIDEBAR_VARIANT}
                                              color={widget.iconColor}
                                              sx={{
                                                width: 32,
                                                height: 32,
                                                p: 2.25,
                                              }}
                                              hoverColor={widget.iconColor}
                                              onClick={() => handleOpenWidget(widget)}
                                            >
                                              <widget.icon />
                                            </ToolkitIcon>
                                            {/* </Badge> */}
                                          </BenefitPreviewGate>
                                        ) : (
                                          <>
                                            {/* punto del sidebar desactivado, ahora se usa el dock */}
                                            <ToolkitIcon
                                              expandedItem={false}
                                              sidebarVariant={SIDEBAR_VARIANT}
                                              color={widget.iconColor}
                                              sx={{
                                                width: 32,
                                                height: 32,
                                                p: 2.25,
                                              }}
                                              hoverColor={widget.iconColor}
                                              onClick={() => handleOpenWidget(widget)}
                                            >
                                              <widget.icon />
                                            </ToolkitIcon>
                                            {/* </Badge> */}
                                          </>
                                        )}
                                      </Tooltip>
                                    </ListItem>
                                  );
                                })}
                            </Box>
                          )}
                          {/* Toolkit Section - Expanded */}
                          {page.isToolkit && expandItem && (
                            <Collapse in={toolkitExpanded && expandItem} timeout="auto" unmountOnExit>
                              <ToolkitSection sidebarVariant={SIDEBAR_VARIANT}>
                                {toolkitWidgetsNew
                                  .filter((widget) => widget.key.startsWith("Dashboard") && (!widget.roles || widget.roles.includes(role)))
                                  .map((widget) => {
                                    // const minimized = isMinimized(widget.key); // punto del sidebar desactivado
                                    return widget.benefit ? (
                                      <BenefitPreviewGate
                                        benefit={widget.benefit}
                                        featureName={widget.label}
                                        onPreview={() => handleOpenWidget({ ...widget, isPreviewMode: true })}
                                        overlay={false}
                                        showWhenBlocked={EShowMode.NULL}
                                      >
                                        <ToolkitItem
                                          sidebarVariant={SIDEBAR_VARIANT}
                                          key={widget.key}
                                          disablePadding
                                          onClick={() => handleOpenWidget(widget)}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              width: "100%",
                                              position: "relative",
                                            }}
                                          >
                                            {/* punto del sidebar desactivado, ahora se usa el dock */}
                                            <ToolkitIcon expandedItem={true} sidebarVariant={SIDEBAR_VARIANT} color={widget.iconColor}>
                                              <widget.icon />
                                            </ToolkitIcon>
                                            {/* </Badge> */}
                                            <ListItemText
                                              primary={widget.label}
                                              sx={{
                                                color: colors.gray.base,
                                                flex: 1,
                                                "& .MuiListItemText-primary": {
                                                  fontSize: 14,
                                                  fontWeight: 600,
                                                },
                                              }}
                                            />
                                          </Box>
                                        </ToolkitItem>
                                      </BenefitPreviewGate>
                                    ) : (
                                      <ToolkitItem
                                        sidebarVariant={SIDEBAR_VARIANT}
                                        key={widget.key}
                                        disablePadding
                                        onClick={() => handleOpenWidget(widget)}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            width: "100%",
                                            position: "relative",
                                          }}
                                        >
                                          {/* punto del sidebar desactivado, ahora se usa el dock */}
                                          <ToolkitIcon expandedItem={true} sidebarVariant={SIDEBAR_VARIANT} color={widget.iconColor}>
                                            <widget.icon />
                                          </ToolkitIcon>
                                          {/* </Badge> */}
                                          <ListItemText
                                            primary={widget.label}
                                            sx={{
                                              color: colors.gray.base,
                                              flex: 1,
                                              "& .MuiListItemText-primary": {
                                                fontSize: 14,
                                                fontWeight: 600,
                                              },
                                            }}
                                          />
                                        </Box>
                                      </ToolkitItem>
                                    );
                                  })}
                              </ToolkitSection>
                            </Collapse>
                          )}
                          {/* Organization Section - Expanded */}
                          {page.isOrganization && (
                            <Collapse in={organizationExpanded && expandItem} timeout="auto" unmountOnExit>
                              <ToolkitSection sidebarVariant={SIDEBAR_VARIANT}>
                                {toolkitWidgetsNew
                                  .filter((widget) => widget.key.startsWith("Organization") && (!widget.roles || widget.roles.includes(role)))
                                  .map((widget) => {
                                    // const minimized = isMinimized(widget.key); // punto del sidebar desactivado

                                    return widget.benefit ? (
                                      <BenefitPreviewGate
                                        benefit={widget.benefit}
                                        featureName={widget.label}
                                        onPreview={() => handleOpenOrganizationWidget({ ...widget, isPreviewMode: true })}
                                        overlay={false}
                                        showWhenBlocked={EShowMode.NULL}
                                      >
                                        <ToolkitItem
                                          sidebarVariant={SIDEBAR_VARIANT}
                                          key={widget.key}
                                          disablePadding
                                          onClick={() => handleOpenOrganizationWidget(widget)}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              width: "100%",
                                              position: "relative",
                                            }}
                                          >
                                            {/* punto del sidebar desactivado, ahora se usa el dock */}
                                            <ToolkitIcon expandedItem={true} sidebarVariant={SIDEBAR_VARIANT} color={widget.iconColor}>
                                              <widget.icon />
                                            </ToolkitIcon>
                                            {/* </Badge> */}
                                            <ListItemText
                                              primary={widget.label}
                                              sx={{
                                                color: colors.gray.base,
                                                flex: 1,
                                                "& .MuiListItemText-primary": {
                                                  fontSize: 14,
                                                  fontWeight: 600,
                                                },
                                              }}
                                            />
                                          </Box>
                                        </ToolkitItem>
                                      </BenefitPreviewGate>
                                    ) : (
                                      <ToolkitItem
                                        sidebarVariant={SIDEBAR_VARIANT}
                                        key={widget.key}
                                        disablePadding
                                        onClick={() => handleOpenOrganizationWidget(widget)}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            width: "100%",
                                            position: "relative",
                                          }}
                                        >
                                          {/* punto del sidebar desactivado, ahora se usa el dock */}
                                          <ToolkitIcon expandedItem={true} sidebarVariant={SIDEBAR_VARIANT} color={widget.iconColor}>
                                            <widget.icon />
                                          </ToolkitIcon>
                                          {/* </Badge> */}
                                          <ListItemText
                                            primary={widget.label}
                                            sx={{
                                              color: colors.gray.base,
                                              flex: 1,
                                              "& .MuiListItemText-primary": {
                                                fontSize: 14,
                                                fontWeight: 600,
                                              },
                                            }}
                                          />
                                        </Box>
                                      </ToolkitItem>
                                    );
                                  })}
                              </ToolkitSection>
                            </Collapse>
                          )}
                          {/* Organization Section - Collapsed */}
                          {page.isOrganization && !expandItem && (
                            <Box sx={{ ml: 0 }}>
                              {toolkitWidgetsNew
                                .filter((widget) => widget.key.startsWith("Organization") && (!widget.roles || widget.roles.includes(role)))
                                .map((widget) => {
                                  // const minimized = isMinimized(widget.key); // punto del sidebar desactivado
                                  return (
                                    <ListItem
                                      key={widget.key}
                                      sx={{
                                        p: 0,
                                        my: 2,
                                        position: "relative",
                                        // border: "1px solid red",
                                        display: "flex",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Tooltip
                                        title={widget.label}
                                        placement="right"
                                        PopperProps={{
                                          sx: { zIndex: 99999 },
                                          modifiers: [
                                            {
                                              name: "offset",
                                              options: {
                                                offset: [0, -5],
                                              },
                                            },
                                          ],
                                        }}
                                      >
                                        {widget.benefit ? (
                                          <BenefitPreviewGate
                                            benefit={widget.benefit}
                                            featureName={widget.label}
                                            onPreview={() => handleOpenOrganizationWidget({ ...widget, isPreviewMode: true })}
                                            overlay={false}
                                            showWhenBlocked={EShowMode.NULL}
                                          >
                                            {/* punto del sidebar desactivado, ahora se usa el dock */}
                                            <ToolkitIcon
                                              expandedItem={false}
                                              sidebarVariant={SIDEBAR_VARIANT}
                                              color={widget.iconColor}
                                              sx={{
                                                width: 32,
                                                height: 32,
                                                p: 2.25,
                                              }}
                                              onClick={() => handleOpenOrganizationWidget(widget)}
                                            >
                                              <widget.icon />
                                            </ToolkitIcon>
                                            {/* </Badge> */}
                                          </BenefitPreviewGate>
                                        ) : (
                                          <>
                                            {/* punto del sidebar desactivado, ahora se usa el dock */}
                                            <ToolkitIcon
                                              expandedItem={false}
                                              sidebarVariant={SIDEBAR_VARIANT}
                                              color={widget.iconColor}
                                              sx={{
                                                width: 32,
                                                height: 32,
                                                p: 2.25,
                                              }}
                                              onClick={() => handleOpenOrganizationWidget(widget)}
                                            >
                                              <widget.icon />
                                            </ToolkitIcon>
                                            {/* </Badge> */}
                                          </>
                                        )}
                                      </Tooltip>
                                    </ListItem>
                                  );
                                })}
                            </Box>
                          )}
                        </React.Fragment>
                      ))}
                  </List>
                  {/* About Section - Inside Scrollable Box */}
                  {expandItem && (
                    <Box
                      sx={{
                        px: 1,
                        py: 2,
                        backgroundColor: "inherit",
                        direction: "ltr",
                        marginTop: "auto",
                      }}
                    >
                      <Divider />
                      <Box
                        sx={{
                          pt: 1.5,
                          mb: -1.5,
                          pb: 0,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignContent: "center",
                          // border: "1px solid red"
                        }}
                      >
                        {getSideBarOptions.map((item) => (
                          <Button
                            key={item.key}
                            onClick={() => openModal(item.modalKey)}
                            sx={{
                              justifyContent: "center",
                              textTransform: "none",
                              color: colors.gray.base,
                              fontSize: 12,
                              py: 0.25,
                              borderRadius: 1,
                              "&:hover": { backgroundColor: colors.gray[50] },
                            }}
                          >
                            {item.label}
                            {item.withHelpIcon && <HelpOutlineIcon sx={{ fontSize: 14, color: colors.gray[400] }} />}
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </StyledDrawer>
              {(role === IRoles.Business ||
                role === IRoles.Admin ||
                role === IRoles.VR ||
                role === IRoles.Manager ||
                role === IRoles.Other ||
                role === IRoles.SuperAdmin) && (
                <CollapseToggleButton
                  open={expandItem}
                  onClick={() => {
                    handleExpand(expandItem);
                  }}
                >
                  {!expandItem ? <ViewSidebar /> : <Cancel fontSize="small" />}
                </CollapseToggleButton>
              )}
            </Box>
          </SidebarWrapper>

          <Popover
            open={openOtherCompanies}
            anchorEl={otherCompaniesAnchor}
            onClose={() => setOtherCompaniesAnchor(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  width: 320,
                  px: 2,
                  py: 1.5,
                  borderRadius: "16px",
                  border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                  overflow: "visible",
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  color: "text.secondary",
                }}
              >
                Active companies
              </Typography>

              <Typography
                sx={{
                  fontSize: 10,
                  color: "text.disabled",
                }}
              >
                {otherCompanyItems.length} companies
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                overflowX: "auto",
                pt: 1,
                pb: 0.5,

                "&::-webkit-scrollbar": {
                  height: 3,
                },
              }}
            >
              {otherCompanyItems.map((company) => (
                <Box
                  key={company.id}
                  sx={{
                    width: 58,
                    flexShrink: 0,
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: 40,
                      height: 40,
                      mx: "auto",
                    }}
                  >
                    <Avatar
                      src={company.avatar || undefined}
                      sx={{
                        width: 40,
                        height: 40,
                        border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.10)",
                        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                        color: "var(--fx-surface-text, #0F172A)",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {company.name
                        ?.split(/\s+/)
                        .slice(0, 2)
                        .map((part) => part[0])
                        .join("")
                        .toUpperCase()}
                    </Avatar>

                    <Box
                      sx={{
                        position: "absolute",
                        top: -7,
                        right: -9,
                        minWidth: 18,
                        height: 18,
                        px: 0.4,
                        borderRadius: "999px",
                        backgroundColor: "#f97316",
                        color: "#fff",
                        fontSize: 9,
                        fontWeight: 700,
                        border: "2px solid var(--fx-surface-base, #FFFFFF)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {company.notifications > 99 ? "99+" : company.notifications}
                    </Box>
                  </Box>

                  <Typography
                    noWrap
                    sx={{
                      mt: 0.75,
                      width: "100%",
                      fontSize: 9,
                      color: "text.secondary",
                    }}
                  >
                    {company.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Popover>
          <ConnectBusinessExit open={openExit} setOpen={setOpenExit} continueAction={continueAction} setContinueAction={setContinueAction} />

          {/* Headless Modal Templates for sidebar widgets */}

          <HeadlessModalTemplates toolkitWidgets={toolkitWidgetsNew} />
        </>
      )}
    </motion.div>
  );
};
